import React, { useState } from "react";

// Función utilitaria para saber si un post está expirado
const isExpired = (fechaFin) => {
  if (!fechaFin) return false;
  const now = new Date();
  const fin = new Date(fechaFin);
  return fin < now;
};

const AllPost = ({ posts, onEdit, onDelete }) => {
  const [filtroVisibilidad, setFiltroVisibilidad] = useState("todos");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");

  // Obtener lista de categorías únicas para filtros
  const categoriasUnicas = [
    ...new Set(posts.map((p) => p.categoria).filter(Boolean)),
  ];

  // Agrupar por objetivo
  const agrupadosPorObjetivo = posts.reduce((acc, post) => {
    if (post.estado === "eliminado" || post.deleted) return acc;

    const objetivo = post.objetivo || "Sin objetivo";
    if (!acc[objetivo]) acc[objetivo] = [];
    acc[objetivo].push(post);
    return acc;
  }, {});

  // Función de filtrado
  const filtrarPosts = (lista) => {
    return lista.filter((post) => {
      if (
        filtroVisibilidad !== "todos" &&
        ((filtroVisibilidad === "publicos" && post.visibilidad !== true) ||
          (filtroVisibilidad === "ocultos" && post.visibilidad !== false))
      ) {
        return false;
      }

      if (
        filtroCategoria !== "todas" &&
        post.categoria !== filtroCategoria
      ) {
        return false;
      }

      return true;
    });
  };

  return (
    <div className="space-y-8 px-2 sm:px-4 md:px-6 lg:px-8">

      {/* Filtros */}
      <div className="flex flex-wrap gap-6 justify-start items-end">
        <div className="flex flex-col">
          <label
            htmlFor="filtroVisibilidad"
            className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300"
          >
            Visibilidad
          </label>
          <select
            id="filtroVisibilidad"
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={filtroVisibilidad}
            onChange={(e) => setFiltroVisibilidad(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="publicos">Públicos</option>
            <option value="ocultos">Ocultos</option>
          </select>
        </div>

        <div className="flex flex-col">
          <label
            htmlFor="filtroCategoria"
            className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300"
          >
            Categoría
          </label>
          <select
            id="filtroCategoria"
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
          >
            <option value="todas">Todas</option>
            {categoriasUnicas.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Listado agrupado por objetivo */}
      {Object.entries(agrupadosPorObjetivo).map(([objetivo, postsGrupo]) => {
        const visibles = filtrarPosts(postsGrupo);

        if (visibles.length === 0) return null;

        return (
          <section
            key={objetivo}
            aria-labelledby={`objetivo-${objetivo.replace(/\s+/g, "-").toLowerCase()}`}
            className="space-y-6"
          >
            <h2
              id={`objetivo-${objetivo.replace(/\s+/g, "-").toLowerCase()}`}
              className="text-2xl font-extrabold text-gray-900 dark:text-gray-100 border-b border-gray-300 dark:border-gray-700 pb-2 flex items-center gap-2"
            >
              <span role="img" aria-label="Objetivo">
                
              </span>{" "}
              Objetivo: {objetivo}
            </h2>

            <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2">
              {visibles.map((post) => {
                const expirado = isExpired(post.fechaFin);

                return (
                  <article
                    key={post.id}
                    className={`rounded-lg shadow-md p-5 flex flex-col justify-between transition-transform transform hover:scale-[1.02] bg-white dark:bg-gray-900 border ${
                      expirado
                        ? "border-gray-300 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                        : "border-gray-200 dark:border-gray-700"
                    }`}
                    aria-label={`Publicación: ${post.title}`}
                  >
                    {/* Cabecera */}
                    <header className="flex justify-between items-start">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug max-w-[75%]">
                        {post.title}
                      </h3>
                      {post.destacado && (
                        <span className="inline-block bg-yellow-400 text-yellow-900 dark:text-yellow-800 text-xs font-semibold px-3 py-0.5 rounded-full select-none shadow-sm">
                          ★ Destacado
                        </span>
                      )}
                    </header>

                    {/* Descripción */}
                    {post.description && (
                      <p className="mt-3 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        {post.description}
                      </p>
                    )}

                    {/* Imágenes */}
                    {post.images?.length > 0 && (
                      <div className="mt-4 flex space-x-3 overflow-x-auto no-scrollbar">
                        {post.images.map((imgUrl, i) => (
                          <img
                            key={i}
                            src={imgUrl}
                            alt={`Imagen ${i + 1} de ${post.title}`}
                            className="w-24 h-24 rounded-md object-cover flex-shrink-0 shadow-md"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    )}

                    {/* Datos adicionales */}
                    <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600 dark:text-gray-400">
                      {post.categoria && (
                        <>
                          <dt className="font-semibold">Categoría:</dt>
                          <dd>{post.categoria}</dd>
                        </>
                      )}
                      {post.descuento && (
                        <>
                          <dt className="font-semibold">Oferta:</dt>
                          <dd>{post.descuento}</dd>
                        </>
                      )}
                      {post.fechaInicio && (
                        <>
                          <dt className="font-semibold">Desde:</dt>
                          <dd>{new Date(post.fechaInicio).toLocaleDateString()}</dd>
                        </>
                      )}
                      {post.fechaFin && (
                        <>
                          <dt className="font-semibold">Hasta:</dt>
                          <dd>{new Date(post.fechaFin).toLocaleDateString()}</dd>
                        </>
                      )}
                      {post.visibilidad !== undefined && (
                        <>
                          <dt className="font-semibold">Visibilidad:</dt>
                          <dd>{post.visibilidad ? "Público" : "Oculto"}</dd>
                        </>
                      )}
                    </dl>

                    {/* CTA */}
                    {post.ctaTexto && post.ctaLink && (
                      <a
                        href={post.ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 inline-block bg-indigo-600 text-white font-medium text-sm px-5 py-2 rounded-md shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        aria-label={`Ir a ${post.ctaTexto}`}
                      >
                        {post.ctaTexto}
                      </a>
                    )}

                    {/* Botones */}
                    <div className="mt-5 flex space-x-3 justify-end">
                      <button
                        onClick={() => onEdit(post)}
                        className="bg-yellow-400 hover:bg-yellow-500 text-black font-semibold px-4 py-2 rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        aria-label={`Editar publicación: ${post.title}`}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => onDelete(post)}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-600"
                        aria-label={`Eliminar publicación: ${post.title}`}
                      >
                        Eliminar
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default AllPost;
